/* eslint-disable react/display-name */
import MaterialTable, { MTableBody } from '@material-table/core'
import {
  Box,
  Button,
  createStyles,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core'
import { InfoOutlined } from '@material-ui/icons'
import { MoneyInput } from 'components/Common/Input/MoneyInput'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { BomItemController } from 'controllers/Project/BomItemController'
import { useController } from 'customHooks/useController'
import { useMaterialTableLocalization } from 'customHooks/useMaterialTableLocalization'
import { ProjectEditableStateSelector } from 'features/BillOfMaterials/store/selectors/ProjectEditableStateSelector'
import _ from 'lodash'
import { MoneyString } from 'model/Money'
import { MoneyPerQuantityString } from 'model/MoneyPerQuantity'
import { BoMItemType } from 'model/Project/BoMItemRow'
import { TimeString } from 'model/Quantity'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import {
  IssueDto,
  PriceRowDto,
  PriceScope,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  APICall,
  ErrorMessage,
  ShowException,
} from 'store/Application/appActions'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { ReactComponent as InfoOverIcon } from '_icons/info_over-sm.svg'
import { ReactComponent as DownloadIcon } from '_icons/project-menu/download.svg'
import CostPriceExplanationModal from '../../RowDetails/CostPriceExplanationModal'

type Props = {
  priceDetails: PriceRowDto[]
  rowIssues: IssueDto[]
  rowId: string
  boMItemType: BoMItemType
  onEditFinished?: (newData: PriceRowDto) => void
  loading?: boolean
  financialUnit: 'perUnit' | 'total'
  onChangeFinancialUnit: (unit: 'perUnit' | 'total') => void
  totalProjectQuantity: number
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    financialDetailsRoot: {
      height: '100%',
      position: 'relative',
      '& small': {
        display: 'block',
      },
      '& .MuiPaper-root': {
        boxShadow: 'none !important',
        border: `2px solid ${theme.palette.divider}`,
      },
      // '& .MuiTableContainer-root': {
      //   height: '100%',
      // },
      '& .MuiTable-root tr td:last-child': {
        textAlign: 'right !important',
        justifyContent: 'center !important',
        // paddingRight: theme.spacing(3),
      },
      '& table': {
        height: '100%',
        '& tr th, tr td': {
          whiteSpace: 'pre',
          fontSize: theme.typography.caption.fontSize,
          padding: '2px',
          '& button': {
            borderRadius: 0,
          },
        },
        '& tr:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '& tr th': {
          fontWeight: theme.typography.fontWeightBold,
        },
        '& tr td svg': {
          width: `${theme.spacing(3)}px`,
        },
        '& tbody': {
          height: '100%',
        },
        '& tbody tr th': {
          whiteSpace: 'pre',
          paddingLeft: '1em',
          '& svg': {
            width: '3em',
          },
        },
        '& tfoot': {
          position: 'sticky',
          backgroundColor: theme.palette.background.paper,
          bottom: 0,
          left: 0,
          right: 0,
          '& tr': {
            // : theme.shadows[10],
            borderTop: `2px solid ${'theme.palette.divider'}`,
          },
        },
        '& tfoot tr th, tfoot tr td': {
          fontSize: theme.typography.body1.fontSize,
          fontWeight: theme.typography.fontWeightRegular,
          color: theme.palette.text.primary,
        },
      },
    },
    sharedCostRow: {
      backgroundColor: theme.palette.action.hover,
      '& td,th,p': {
        color: `${theme.palette.text.disabled} !important`,
      },
    },
    manuallySetButton: {
      padding: 0,
      justifyContent: 'flex-end',
    },
  })
)

const FinancialDetailsTable = (props: Props) => {
  const cn = useStyles()
  const theme = useTheme()

  const isEditable = useAppSelector(ProjectEditableStateSelector)

  const [activeCostPriceExplanation, setActiveCostPriceExplanation] =
    useState(null)

  const materialTableLocalization = useMaterialTableLocalization()

  const { t } = useTranslation()

  const { partyId: organizationId, projectId } =
    useOrganizationAndProjectContext()

  const { controller: bomItemController } = useController(
    () => new BomItemController(organizationId, projectId)
  )

  const dispatcher = useAppDispatch()

  const downloadCostDriverCalculators = (
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) => {
    dispatcher(
      APICall({
        label: `downloadCostDriverCalculators_${props.rowId}_${primaryWorkingStep}_${secondaryWorkingStep}`,
        configuration: {
          method: 'GET',
          url: `api/parties/${organizationId}/projects/${projectId}/items/${props.rowId}/workingSteps/costDriverCalculators?primaryWorkingStep=${primaryWorkingStep}&secondaryWorkingStep=${secondaryWorkingStep}`,
          isFileDownload: true,
          onProgress: (progress) =>
            console.info(
              'downloadCostDriverCalculators -> progress:',
              progress
            ),
          onError: (message) => {
            ErrorMessage('cost drivers', message)
          },
        },
      })
    )
  }

  const handleEdit = async (newData: PriceRowDto, oldData: PriceRowDto) => {
    const priceScope =
      props.financialUnit === 'perUnit' ? PriceScope.Unit : PriceScope.Total

    if (
      !_.isEqual(
        newData.productionTimeManuallySet,
        oldData.productionTimeManuallySet
      )
    ) {
      try {
        await bomItemController.ManualSetProductionTime(props.rowId, {
          primaryWorkingStep: newData.workingStep.primaryWorkingStep,
          secondaryWorkingStep: newData.workingStep.secondaryWorkingStep,
          productionTime: newData.productionTimeManuallySet,
          priceScope,
        })
        props.onEditFinished && props.onEditFinished(newData)
      } catch (ex) {
        ShowException('project', ex)
      }
    }

    if (
      !_.isEqual(
        newData.additionalProductionCostsManuallySet,
        oldData.additionalProductionCostsManuallySet
      )
    ) {
      try {
        await bomItemController.ManualSetAdditionalCosts(props.rowId, {
          primaryWorkingStep: newData.workingStep.primaryWorkingStep,
          secondaryWorkingStep: newData.workingStep.secondaryWorkingStep,
          additionalCosts: newData.additionalProductionCostsManuallySet,
          priceScope,
        })
        props.onEditFinished && props.onEditFinished(newData)
      } catch (ex) {
        ShowException('project', ex)
      }
    }

    if (!_.isEqual(newData.costPrice, oldData.costPrice)) {
      try {
        await bomItemController.SetMaterialCostPrice(
          props.rowId,
          newData.costPrice,
          priceScope
        )

        props.onEditFinished && props.onEditFinished(newData)
      } catch (ex) {
        ShowException('project', ex)
      }
    }

    // if (
    //   !_.isEqual(
    //     newData.discountOrSurchargeRatio,
    //     oldData.discountOrSurchargeRatio
    //   )
    // ) {
    //   try {
    //     await bomItemController.SetBomItemDiscountOrSurcharge(
    //       props.rowId,
    //       newData.discountOrSurchargeRatio.value,
    //       priceScope
    //     )

    //     props.onEditFinished && props.onEditFinished(newData)
    //   } catch (ex) {
    //     ShowException('project', ex)
    //   }
    // }

    return Promise.resolve()
  }

  const handleResetProductionTime = async (rowData: PriceRowDto) => {
    try {
      await bomItemController.DeleteManualSetProductionTime(
        props.rowId,
        rowData.workingStep.primaryWorkingStep,
        rowData.workingStep.secondaryWorkingStep
      )
      props.onEditFinished && props.onEditFinished(rowData)
    } catch (ex) {
      ShowException('project', ex)
    }
    // openDialog('ConfirmationDialog', {
    //   title: t(
    //     'project:reset-production-time',
    //     'reset to estimated production time ({{time}})',
    //     {
    //       time: TimeString(rowData.productionTime),
    //     }
    //   ),
    //   onConfirm: async () => {
    //     try {
    //       await bomItemController.DeleteManualSetProductionTime(
    //         props.rowId,
    //         rowData.workingStep.primaryWorkingStep,
    //         rowData.workingStep.secondaryWorkingStep
    //       )
    //       props.onEditFinished && props.onEditFinished(rowData)
    //     } catch (ex) {
    //       ShowException('project', ex)
    //     }
    //   },
    // })
  }

  const handleResetAdditionalCosts = async (rowData: PriceRowDto) => {
    try {
      await bomItemController.DeleteManualSetAdditionalCosts(
        props.rowId,
        rowData.workingStep.primaryWorkingStep,
        rowData.workingStep.secondaryWorkingStep
      )
      props.onEditFinished && props.onEditFinished(rowData)
    } catch (ex) {
      ShowException('project', ex)
    }
    // openDialog('ConfirmationDialog', {
    //   title: t(
    //     'project:reset-additional-costs',
    //     'reset to estimated additional costs ({{costs}})',
    //     {
    //       costs: MoneyString(rowData.additionalProductionCosts),
    //     }
    //   ),
    //   onConfirm: async () => {
    //     try {
    //       await bomItemController.DeleteManualSetAdditionalCosts(
    //         props.rowId,
    //         rowData.workingStep.primaryWorkingStep,
    //         rowData.workingStep.secondaryWorkingStep
    //       )
    //       props.onEditFinished && props.onEditFinished(rowData)
    //     } catch (ex) {
    //       ShowException('project', ex)
    //     }
    //   },
    // })
  }

  const handleResetMaterialCost = async (rowData: PriceRowDto) => {
    try {
      await bomItemController.DeleteManualSetAdditionalCosts(
        props.rowId,
        rowData.workingStep.primaryWorkingStep,
        rowData.workingStep.secondaryWorkingStep
      )
      props.onEditFinished && props.onEditFinished(rowData)
    } catch (ex) {
      ShowException('project', ex)
    }

    // openDialog('ConfirmationDialog', {
    //   title: t(
    //     'project:reset-cost-price',
    //     'reset to estimated cost price ({{costs}})',
    //     {
    //       costs: MoneyString(rowData.costPrice),
    //     }
    //   ),
    //   onConfirm: async () => {
    //     try {
    //       await bomItemController.DeleteManualSetAdditionalCosts(
    //         props.rowId,
    //         rowData.workingStep.primaryWorkingStep,
    //         rowData.workingStep.secondaryWorkingStep
    //       )
    //       props.onEditFinished && props.onEditFinished(rowData)
    //     } catch (ex) {
    //       ShowException('project', ex)
    //     }
    //   },
    // })
  }

  const totalRow = props.priceDetails?.find((x) => x.isTotalRow)
  const tableData = props.priceDetails?.filter((x) => !x.isTotalRow) || []

  return (
    <div className={cn.financialDetailsRoot}>
      {activeCostPriceExplanation != null && (
        <CostPriceExplanationModal
          isActive={true}
          rowId={props.rowId}
          onClose={() => setActiveCostPriceExplanation(null)}
          workingStepType={activeCostPriceExplanation}
        />
      )}
      <MaterialTable<PriceRowDto>
        data={tableData}
        isLoading={props.loading}
        options={{
          maxBodyHeight: '60vh',
          showTitle: false,
          sorting: false,
          search: false,
          toolbar: false,
          paging: false,
          actionsColumnIndex: -1,
          defaultOrderByCollection: [],
          actionsCellStyle: {
            display: 'flex',
            justifyContent: 'flex-end',
            height: '100%',
          },
          showEmptyDataSourceMessage: false,
          draggable: false,
          rowStyle: (data: PriceRowDto) => {
            if (data.costsAreShared) {
              return {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.text.disabled,
              }
            }
            return null
          },
        }}
        localization={materialTableLocalization}
        columns={[
          {
            // description
            // eslint-disable-next-line react/display-name
            sorting: true,
            title: 'description',
            headerStyle: {
              textAlign: 'center',
              flexDirection: 'row-reverse',
            },
            customSort: (data1, data2) => {
              if (!data1?.isWorkingStep) return -1
              if (!data2?.isWorkingStep) return -1

              return data1.workingStep.key < data2.workingStep.key ? -1 : 1
            },
            render: (rowData) => {
              return (
                <ListItem component="span" dense disableGutters>
                  {rowData?.isWorkingStep && (
                    <ListItemAvatar style={{ minWidth: 0 }}>
                      <WorkingStepIconSvg
                        workingStepType={rowData.workingStep.primaryWorkingStep}
                      />
                    </ListItemAvatar>
                  )}

                  <ListItemText
                    style={{ paddingLeft: '1em' }}
                    primary={
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">
                          {rowData.description}
                          {rowData.isBatchIndependent ? (
                            <Tooltip
                              placement={'top'}
                              arrow
                              title={t(
                                'common:estimation-batch-independent',
                                'estimation and additional costs are quantity independent'
                              )}
                            >
                              <IconButton size="small">
                                <InfoOutlined color="primary" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ marginLeft: '1em' }}
                          color="textSecondary"
                        >
                          {MoneyPerQuantityString(rowData.rate)}
                        </Typography>
                      </Box>
                    }
                    primaryTypographyProps={{
                      component: 'span',
                    }}
                    secondary={
                      rowData.workingStep?.resource?.name && (
                        <>
                          <Typography
                            variant="caption"
                            className="resource-name"
                          >
                            {`${rowData.workingStep.resource.name}`}
                          </Typography>
                        </>
                      )
                    }
                  />
                </ListItem>
              )
            },
          },

          {
            field: 'productionTimeManuallySet',
            title: 'estimation',
            customSort: (data1, data2) => {
              return (
                (data2.productionTimeManuallySet || data2.productionTime)
                  .value -
                (data1.productionTimeManuallySet || data1.productionTime).value
              )
            },
            render: (rowData) => {
              if (!rowData?.isWorkingStep) return null
              return (
                <>
                  <Typography variant="inherit" component="p">
                    {TimeString(
                      rowData.productionTimeManuallySet ||
                        rowData.productionTime
                    )}
                  </Typography>

                  {rowData.productionTimeManuallySet && (
                    <Button
                      size="small"
                      className={cn.manuallySetButton}
                      title="reset to estimated production time"
                      onClick={() => {
                        handleResetProductionTime(rowData)
                      }}
                    >
                      <Typography variant="caption">
                        {TimeString(rowData.productionTime)}
                      </Typography>
                    </Button>
                  )}
                </>
              )
            },

            cellStyle: {
              textAlign: 'right',
            },
            headerStyle: {
              flexDirection: 'row-reverse',
              textAlign: 'right',
            },
            editable: 'always',
            editComponent: (fieldProps) => {
              return fieldProps.rowData?.isWorkingStep ? (
                <div style={{ minWidth: 'fit-content' }}>
                  <TextField
                    value={
                      fieldProps.value?.value.toFixed(0) ||
                      fieldProps.rowData.productionTime.value.toFixed(0)
                    }
                    onChange={(e) =>
                      fieldProps.onChange({
                        ...(fieldProps.value ||
                          fieldProps.rowData.productionTime),
                        value: parseFloat(e.currentTarget.value),
                      })
                    }
                    type="number"
                    inputProps={{
                      style: { textAlign: 'right' },
                      step: 1,
                      min: 0,
                      max: 500,
                    }}
                    fullWidth
                  />
                  <Select
                    value={
                      fieldProps.value?.unit ||
                      fieldProps.rowData.productionTime.unit
                    }
                    onChange={(ev) => {
                      fieldProps.onChange({
                        ...(fieldProps.value ||
                          fieldProps.rowData.productionTime),
                        unit: ev.target.value,
                      })
                    }}
                    style={{ paddingLeft: '1ch' }}
                  >
                    {(
                      fieldProps.value || fieldProps.rowData.productionTime
                    ).selectableAbbreviations?.map((x) => {
                      return (
                        <MenuItem key={x} value={x}>
                          {x}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </div>
              ) : null
            },
          },
          // {
          //   field: 'rate.money.value',
          //   title: 'rate',
          //   sorting: false,
          //   render: (rowData) => MoneyPerQuantityString(rowData.rate),
          //   cellStyle: {
          //     textAlign: 'right',
          //   },
          //   headerStyle: {
          //     flexDirection: 'row-reverse',
          //     textAlign: 'right',
          //   },
          //   editable: 'never',
          // },
          {
            field: 'productionTimeCosts.value',
            title: 'costs\n[prod time]',
            render: (rowData) => (
              <>
                <Typography variant="inherit" component="p">
                  {MoneyString(rowData.productionTimeCosts)}
                </Typography>
              </>
            ),
            cellStyle: {
              textAlign: 'right',
            },
            headerStyle: {
              flexDirection: 'row-reverse',
              textAlign: 'right',
            },
            editable: 'never',
          },

          {
            field: 'additionalProductionCostsManuallySet',
            title: 'costs\n[additional]',
            render: (rowData) => (
              <>
                <Typography variant="inherit" component="p">
                  {MoneyString(
                    rowData.additionalProductionCostsManuallySet ||
                      rowData.additionalProductionCosts
                  )}
                </Typography>
                {rowData.additionalProductionCostsManuallySet && (
                  <Button
                    size="small"
                    className={cn.manuallySetButton}
                    title="reset to estimated additional costs"
                    onClick={() => {
                      handleResetAdditionalCosts(rowData)
                    }}
                  >
                    <Typography variant="caption">
                      {MoneyString(rowData.additionalProductionCosts)}
                    </Typography>
                  </Button>
                )}
              </>
            ),
            cellStyle: {
              textAlign: 'right',
            },
            headerStyle: {
              flexDirection: 'row-reverse',
              textAlign: 'right',
            },
            editable: 'onUpdate',
            editComponent: (fieldProps) =>
              fieldProps.rowData?.isWorkingStep ? (
                <Box paddingLeft={'1em'}>
                  <MoneyInput
                    value={
                      fieldProps.value ||
                      fieldProps.rowData.additionalProductionCosts
                    }
                    onChange={(e) => fieldProps.onChange(e.currentTarget.value)}
                  />
                </Box>
              ) : (
                <Typography variant="inherit" component={'p'}>
                  {MoneyString(
                    fieldProps.value ||
                      fieldProps.rowData.additionalProductionCosts
                  )}
                </Typography>
              ),
          },
          {
            field: 'costPrice',
            title: 'costs\n[total]',
            render: (rowData) => (
              <>
                <Typography variant="inherit" component="p">
                  {MoneyString(rowData.costPrice)}
                </Typography>
                {rowData.costPriceIsManuallySet && (
                  <Button
                    size="small"
                    className={cn.manuallySetButton}
                    title="reset to estimated cost price"
                    onClick={() => {
                      handleResetMaterialCost(rowData)
                    }}
                  >
                    <Typography variant="caption">reset</Typography>
                  </Button>
                )}
              </>
            ),
            cellStyle: {
              textAlign: 'right',
            },
            editable: 'onUpdate',
            editComponent: (fieldProps) =>
              !fieldProps.rowData?.isWorkingStep ? (
                <Box paddingLeft={'1em'}>
                  <MoneyInput
                    value={fieldProps.value || fieldProps.rowData.costPrice}
                    onChange={(e) => fieldProps.onChange(e.currentTarget.value)}
                  />
                </Box>
              ) : (
                <Typography variant="inherit" component="p">
                  {MoneyString(fieldProps.rowData.costPrice)}
                </Typography>
              ),
            headerStyle: {
              textAlign: 'right',
              flexDirection: 'row-reverse',
            },
          },
          // {
          //   field: 'discountOrSurchargeRatio',
          //   title: 'discount/surcharge',
          //   cellStyle: {
          //     textAlign: 'right',
          //   },
          //   editable: 'onUpdate',
          //   headerStyle: {
          //     flexDirection: 'row-reverse',
          //     textAlign: 'right',
          //   },
          //   render: (rowData) => (
          //     <>
          //       <Typography variant="inherit" component="p">
          //         {MoneyString(rowData.discountOrSurchargeValue)}
          //       </Typography>
          //       <Typography variant="caption" color="textSecondary">
          //         {QuantityString(rowData.discountOrSurchargeRatio)}
          //       </Typography>
          //     </>
          //   ),
          //   editComponent: (fieldProps) =>
          //     fieldProps.rowData.canManualEditSurchargeDiscount ? (
          //       <Box paddingLeft={'1em'}>
          //         <DecimalTextField
          //           value={
          //             fieldProps.value.value ||
          //             fieldProps.rowData.discountOrSurchargeRatio.value
          //           }
          //           onChangeValue={(e) =>
          //             fieldProps.onChange({ value: e.numericValue })
          //           }
          //           suffix={'%'}
          //           minValue={-100}
          //           fractionDigits={2}
          //         />
          //       </Box>
          //     ) : (
          //       <>
          //         <Typography variant="inherit" component="p">
          //           {MoneyString(fieldProps.rowData.discountOrSurchargeValue)}
          //         </Typography>
          //         <Typography variant="caption" color="textSecondary">
          //           {QuantityString(
          //             fieldProps.rowData.discountOrSurchargeRatio
          //           )}
          //         </Typography>
          //       </>
          //     ),
          // },
          {
            field: 'salesPrice.value',
            title: 'sales price',
            cellStyle: {
              textAlign: 'right',
            },
            editable: 'never',
            headerStyle: {
              flexDirection: 'row-reverse',
              textAlign: 'right',
            },
            render: (rowData) => MoneyString(rowData.salesPrice),
          },
        ]}
        editable={{
          onRowUpdate: handleEdit,
          isEditHidden: (rowData) =>
            !isEditable || !rowData.canManualEditCostPrice,
        }}
        actions={[
          (rowData) =>
            !rowData?.isWorkingStep && {
              icon: () => <InfoOverIcon />,
              onClick: () =>
                setActiveCostPriceExplanation(
                  rowData.workingStep.primaryWorkingStep
                ),
            },
          (rowData) =>
            rowData?.isWorkingStep && {
              icon: () => <DownloadIcon />,
              onClick: () =>
                downloadCostDriverCalculators(
                  rowData.workingStep.primaryWorkingStep,
                  rowData.workingStep.secondaryWorkingStep
                ),
            },
          ,
        ]}
        components={{
          Body: (bodyProps) => (
            <>
              <MTableBody {...bodyProps} />
              <tfoot>
                <tr>
                  <th>
                    <>
                      <Select
                        value={props.financialUnit}
                        onChange={(ev) =>
                          props.onChangeFinancialUnit(
                            ev.target.value as 'perUnit' | 'total'
                          )
                        }
                        style={{ marginLeft: '1ch' }}
                        renderValue={(value) => (
                          <Typography variant="body2" color="textPrimary">
                            {value === 'perUnit'
                              ? t('common:perUnit', 'per unit')
                              : t('common:total') +
                                ` (x${props.totalProjectQuantity})`}
                          </Typography>
                        )}
                      >
                        <MenuItem value={'perUnit'}>
                          {t('common:perUnit', 'per unit')}
                        </MenuItem>
                        <MenuItem value={'total'}>
                          {t('common:total') +
                            ` (x${props.totalProjectQuantity})`}
                        </MenuItem>
                      </Select>
                    </>
                  </th>
                  <td style={{ textAlign: 'right' }}>
                    {TimeString(totalRow.productionTime)}
                  </td>

                  <td style={{ textAlign: 'right' }}>
                    {MoneyString(totalRow.productionTimeCosts)}
                  </td>

                  <td style={{ textAlign: 'right' }}>
                    {MoneyString(totalRow.additionalProductionCosts)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {MoneyString(totalRow.costPrice)}
                  </td>
                  {/* <td style={{ textAlign: 'right' }}>
                    {MoneyString(totalRow.discountOrSurchargeValue)}
                    <small>
                      {QuantityString(totalRow.discountOrSurchargeRatio)}
                    </small>
                  </td> */}
                  <td style={{ textAlign: 'right' }}>
                    {MoneyString(totalRow.salesPrice)}
                  </td>
                  <td />
                  {/* <td>
                    <IconButton>
                      <MoreItemsMenu open={footerOpen} setOpen={setFooterOpen}>
                        <MenuItem
                          onClick={() => setShowPricePerPart(true)}
                        ></MenuItem>
                      </MoreItemsMenu>
                    </IconButton>
                  </td> */}
                </tr>
              </tfoot>
            </>
          ),
        }}
      />
    </div>
  )
}

export { FinancialDetailsTable }
